<template>
  <div
    class="min-h-full bg-slate-50/60 dark:bg-[#0B1120] text-slate-900 dark:text-[#F1F5F9] pb-20 transition-colors"
  >
    <div class="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
      <!-- Hero Header -->
      <div class="text-center space-y-3 max-w-2xl mx-auto">
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/40"
        >
          <Radio class="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
          Hardware & Deployment Modes
        </span>
        <h1
          class="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight"
        >
          Attendance Methods
        </h1>
        <p class="text-sm sm:text-base text-slate-500 dark:text-slate-400">
          Choose the attendance capture workflow tailored to your institution's infrastructure and throughput.
        </p>
      </div>

      <!-- ========== METHOD 1: MOBILE TAP METHOD ========== -->
      <section
        class="bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/90 dark:border-[#263449] p-6 sm:p-10 shadow-xs space-y-8"
      >
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100 dark:border-[#263449]">
          <div class="flex items-center gap-3">
            <div
              class="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-900/40"
            >
              <Smartphone class="w-6 h-6" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h2 class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  Mobile Tap Method
                </h2>
                <span class="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40">
                  Zero Hardware Cost
                </span>
              </div>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Teacher-assisted attendance marking with NFC mobile phones
              </p>
            </div>
          </div>
        </div>

        <!-- 3 Process Steps -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="bg-slate-50/70 dark:bg-[#18243A]/50 rounded-2xl p-5 border border-slate-200/70 dark:border-[#263449] space-y-2">
            <div class="flex items-center justify-between">
              <span class="w-7 h-7 rounded-lg bg-indigo-600 text-white text-xs font-black flex items-center justify-center">1</span>
              <Users class="w-4 h-4 text-slate-400" />
            </div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white pt-1">
              Student Approaches Teacher
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Student brings their physical NFC smart card or student ID badge into classroom.
            </p>
          </div>

          <div class="bg-slate-50/70 dark:bg-[#18243A]/50 rounded-2xl p-5 border border-slate-200/70 dark:border-[#263449] space-y-2">
            <div class="flex items-center justify-between">
              <span class="w-7 h-7 rounded-lg bg-indigo-600 text-white text-xs font-black flex items-center justify-center">2</span>
              <Smartphone class="w-4 h-4 text-slate-400" />
            </div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white pt-1">
              Tap Against Mobile Phone
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Teacher launches the app and holds the card against the phone's built-in NFC sensor.
            </p>
          </div>

          <div class="bg-slate-50/70 dark:bg-[#18243A]/50 rounded-2xl p-5 border border-slate-200/70 dark:border-[#263449] space-y-2">
            <div class="flex items-center justify-between">
              <span class="w-7 h-7 rounded-lg bg-emerald-600 text-white text-xs font-black flex items-center justify-center">3</span>
              <CheckCircle2 class="w-4 h-4 text-emerald-500" />
            </div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white pt-1">
              Instant Cloud Confirmation
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Real-time check-in recorded on server with audible chime, timestamp, and audit trail.
            </p>
          </div>
        </div>

        <!-- 3 Feature Chips -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          <div class="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449]">
            <div class="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400">
              <Zap class="w-4 h-4" />
            </div>
            <div>
              <h4 class="text-xs font-bold text-slate-900 dark:text-white">Sub-Second Speed</h4>
              <p class="text-[11px] text-slate-400 dark:text-slate-500">Record check-in in under 400ms</p>
            </div>
          </div>

          <div class="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449]">
            <div class="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
              <ShieldCheck class="w-4 h-4" />
            </div>
            <div>
              <h4 class="text-xs font-bold text-slate-900 dark:text-white">Tamper Proof</h4>
              <p class="text-[11px] text-slate-400 dark:text-slate-500">NFC UID encryption eliminates proxy</p>
            </div>
          </div>

          <div class="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-[#111C30] border border-slate-200/80 dark:border-[#263449]">
            <div class="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
              <BarChart3 class="w-4 h-4" />
            </div>
            <div>
              <h4 class="text-xs font-bold text-slate-900 dark:text-white">Live Roster Sync</h4>
              <p class="text-[11px] text-slate-400 dark:text-slate-500">Instant parent alerts & reporting</p>
            </div>
          </div>
        </div>
      </section>

      <!-- ========== METHOD 2: SMART READER KIOSK METHOD ========== -->
      <section
        class="bg-white dark:bg-[#111C30] rounded-3xl border border-slate-200/90 dark:border-[#263449] p-6 sm:p-10 shadow-xs space-y-8"
      >
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100 dark:border-[#263449]">
          <div class="flex items-center gap-3">
            <div
              class="w-12 h-12 rounded-2xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0 border border-purple-100 dark:border-purple-900/40"
            >
              <Radio class="w-6 h-6" />
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h2 class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  Smart Reader + Door Kiosk
                </h2>
                <span class="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800/40">
                  Fully Autonomous
                </span>
              </div>
              <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                Fixed entrance readers with hands-free automated presence detection
              </p>
            </div>
          </div>
        </div>

        <!-- 3 Process Steps -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="bg-slate-50/70 dark:bg-[#18243A]/50 rounded-2xl p-5 border border-slate-200/70 dark:border-[#263449] space-y-2">
            <div class="flex items-center justify-between">
              <span class="w-7 h-7 rounded-lg bg-purple-600 text-white text-xs font-black flex items-center justify-center">1</span>
              <Radio class="w-4 h-4 text-slate-400" />
            </div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white pt-1">
              Stationary Door Terminal
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Smart IoT readers mounted at each classroom entrance or gate.
            </p>
          </div>

          <div class="bg-slate-50/70 dark:bg-[#18243A]/50 rounded-2xl p-5 border border-slate-200/70 dark:border-[#263449] space-y-2">
            <div class="flex items-center justify-between">
              <span class="w-7 h-7 rounded-lg bg-purple-600 text-white text-xs font-black flex items-center justify-center">2</span>
              <CreditCard class="w-4 h-4 text-slate-400" />
            </div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white pt-1">
              Self-Serve Tap on Entry
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              Student taps their card upon walking through the classroom threshold.
            </p>
          </div>

          <div class="bg-slate-50/70 dark:bg-[#18243A]/50 rounded-2xl p-5 border border-slate-200/70 dark:border-[#263449] space-y-2">
            <div class="flex items-center justify-between">
              <span class="w-7 h-7 rounded-lg bg-emerald-600 text-white text-xs font-black flex items-center justify-center">3</span>
              <CheckCircle2 class="w-4 h-4 text-emerald-500" />
            </div>
            <h3 class="text-sm font-bold text-slate-900 dark:text-white pt-1">
              Autonomous Verification
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              OLED screen displays student name & photo confirmation with green status LED.
            </p>
          </div>
        </div>

        <!-- Hardware & Pricing Overview -->
        <div class="pt-4 border-t border-slate-100 dark:border-[#263449]">
          <h3 class="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider mb-4">
            Hardware Hardware & Tier Pricing
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="bg-slate-50 dark:bg-[#18243A] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] flex items-center justify-between">
              <div>
                <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">RFID / NFC Card</span>
                <h4 class="text-base font-bold text-slate-900 dark:text-white">Student Smart Card</h4>
                <p class="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">Encrypted chip, customizable print</p>
              </div>
              <div class="text-right">
                <span class="text-2xl font-black text-indigo-600 dark:text-indigo-400 font-mono">₹150</span>
                <span class="block text-[10px] text-slate-400">per student</span>
              </div>
            </div>

            <div class="bg-slate-50 dark:bg-[#18243A] p-5 rounded-2xl border border-slate-200/80 dark:border-[#263449] flex items-center justify-between">
              <div>
                <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">IoT Hardware</span>
                <h4 class="text-base font-bold text-slate-900 dark:text-white">Smart Wall Terminal</h4>
                <p class="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">WiFi + Bluetooth, LED status chime</p>
              </div>
              <div class="text-right">
                <span class="text-2xl font-black text-indigo-600 dark:text-indigo-400 font-mono">₹1,500</span>
                <span class="block text-[10px] text-slate-400">per terminal</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
  import {
    Radio,
    Smartphone,
    Users,
    CheckCircle2,
    Zap,
    ShieldCheck,
    BarChart3,
    CreditCard,
  } from "lucide-vue-next";
</script>

<style scoped>
  @keyframes float {
    0%,
    100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-20px);
    }
  }

  @keyframes readerPulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  @keyframes cardTapEnhanced {
    0%,
    100% {
      transform: translateY(0) rotate(-12deg);
    }
    50% {
      transform: translateY(-30px) rotate(-12deg);
    }
  }

  @keyframes ledBlink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }
</style>
